import React, { useEffect } from 'react';
import Autocomplete, { AutocompleteChangeReason } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { PredefinedGrades } from '../../generated/graphql';
import { svgStyles } from '../Common/Common.styled';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Box, Checkbox, CheckboxProps, Divider, InputAdornment, Popper, styled } from '@mui/material';
import { palette } from '../../theme/palette';
import useGradesFilter from '../GradeFilter/grade-filter-hook';
import ClassesIcon from '../Icons/ClassesIcon';
import { SxProps } from '@mui/system';
import Loader from '../Loader/loader';

interface Props {
  error?: boolean;
  className?: string;
  value?: string[] | undefined;
  onChange?: (data: string[] | undefined) => void;
  disabled?: boolean;
  districts?: string[] | undefined,
  states?: string[] | undefined,
  schools?: string[] | undefined,
}

const styles = {
  textField:{
    border: '0px',
    minHeight: '48px',
    height: 'auto',
    '& input.MuiInputBase-input.Mui-disabled' : {
      backgroundColor: palette.customWhite.inputDisabled,
    },
  },
  box: {
    backgroundColor: palette.customWhite.main,
    height: '45px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
};

type GradeValue = PredefinedGrades | null | undefined;

export const PopperStyledComponent = styled(Popper)(({ theme }) => ({
  border: `1px solid ${theme.palette.customBackground.disabled}`,
}));

const GradeMultiSelect = ({
  className,
  onChange,
  value,
  disabled,
  districts,
  states,
  schools,
}: Props) => {

  const {
    grades,
    loading,
  } = useGradesFilter({ states, districts, schools });

  const allGradesOption: PredefinedGrades = {
    id: 'all',
    grade: ' All Grades',
    position: 0,
  };

  const gradeOptions = grades?.length ? [allGradesOption].concat(grades!) : [];

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const [checkAll, setCheckAll] = React.useState(false);

  const onSelectChange = (e:any, gradeList:(any)[], reason: AutocompleteChangeReason) => {
    if (reason === 'clear' || reason === 'removeOption') {
      setCheckAll(false);
    } else if (reason === 'selectOption' && gradeList.length === grades?.length) {
      setCheckAll(true);
    }
    onChange?.(gradeList?.map((grade:any) => grade?.id));
  };

  const checkAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckAll(event.target.checked);
    if (event.target.checked) {
      onChange?.(grades?.map((grade:any) => grade?.id));
    } else {
      onChange?.([]);
    }
  };

  const onDistrictChange = (selectedValue: string[] | undefined) => {
    const gradeIds = grades?.map((grade: any) => grade.id) ?? [];
    const filteredIds = selectedValue?.filter((id) => gradeIds.includes(id)) ?? [];
    const gradesList = grades?.filter((grade) => filteredIds.includes(grade.id));
    if (selectedValue?.length !== grades?.length) setCheckAll(false);
    if (grades) onChange?.(gradesList?.map((grade: any) => grade?.id) ?? []);
  };

  useEffect(() => {
    onDistrictChange(value);
  }, [districts, grades]);

  return (
    <>
      <Loader open={loading} />
      <Autocomplete
        multiple
        className={className}
        disableCloseOnSelect
        options={gradeOptions}
        getOptionLabel={(option: GradeValue) => option?.grade ?? ''}
        isOptionEqualToValue={(option: GradeValue, selected: GradeValue) =>  option?.id === selected?.id}
        disabled={loading || disabled}
        onChange={onSelectChange}
        value={grades?.filter((grade) => value?.includes(grade?.id)) ?? []}
        renderOption={(props, option: GradeValue, { selected }) => (
          option?.id === 'all' ? (
            <>
              <Box sx={{ ...styles.box } as SxProps}>
                <Checkbox
                  checked={checkAll}
                  onChange={checkAllChange}
                  id="check-all"
                  onMouseDown={(e) => e.preventDefault()}
                />
                All Grades
              </Box>
              <Divider />
            </>
          ) : (
            <Box component="li" {...props} key={option?.id}>
              <Checkbox
                color={'primary' as  CheckboxProps['color']}
                icon={icon}
                checkedIcon={checkedIcon}
                checked={selected}
                sx={{ mr: '8px' }}
              />
              {`${option?.grade ?? ''}`}
            </Box>
          )
        )}
        renderInput={(params) => (
          <>
            <TextField
              {...params}
              sx={{ ...styles.textField }}
              placeholder={params.InputProps.startAdornment ? undefined : 'Select Grade Level/s'}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                style: { height: 'auto', maxHeight: '500px', fontSize: '16px' },
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <ClassesIcon pathfill={palette.customBlue.primaryBlue} sx={{ ...svgStyles.icon }}/>
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          </>
        )}
      />
    </>
  );
};

export default GradeMultiSelect;
